import type { Geometry } from 'ol/geom';
import { Polygon } from 'ol/geom';
import GeoJSON from 'ol/format/GeoJSON';
import { intersects as extentIntersects } from 'ol/extent';

import type { CanvasOgcFeature } from '../ogc/canvas-ogc-features-api.service';

/** OGC bbox query order: minLng, minLat, maxLng, maxLat (CRS84). */
export type OgcBbox = [number, number, number, number];

/** Extract CRS84 bbox from a drawn rectangle polygon. */
export function geometryToOgcBbox(geometry: Geometry | undefined): OgcBbox | null {
  if (!geometry) return null;

  const extent = geometry.getExtent();
  if (extent.length < 4) return null;

  const [minX, minY, maxX, maxY] = extent;
  if (![minX, minY, maxX, maxY].every((n) => Number.isFinite(n))) return null;
  if (minX >= maxX || minY >= maxY) return null;

  return [minX, minY, maxX, maxY];
}

/** Parse resource/collection id from OGC item `describes` link href. */
export function parseDescribesResourceId(links: unknown): string | null {
  if (!Array.isArray(links)) return null;

  for (const link of links) {
    if (!link || typeof link !== 'object') continue;
    const record = link as Record<string, unknown>;
    const rel = String(record['rel'] ?? '').toLowerCase();
    if (rel !== 'describes') continue;

    const href = String(record['href'] ?? '').trim();
    const match = href.match(/\/collections\/([^/?#]+)/i);
    if (match?.[1]) {
      return decodeURIComponent(match[1]);
    }
  }

  return null;
}

/** Client-side check that a feature geometry intersects the drawn bbox extent. */
export function featureGeometryIntersectsBbox(feature: CanvasOgcFeature, bbox: OgcBbox): boolean {
  const geom = feature.geometry;
  if (!geom || typeof geom !== 'object') return false;

  try {
    const format = new GeoJSON();
    const olGeom = format.readGeometry(geom, {
      dataProjection: 'EPSG:4326',
      featureProjection: 'EPSG:4326',
    });
    return extentIntersects(olGeom.getExtent(), bbox);
  } catch {
    return false;
  }
}

/** Collection UUID from `/collections/{id}` in an OGC link href. */
export function extractCollectionIdFromHref(href: string): string | null {
  const trimmed = String(href ?? '').trim();
  const match = trimmed.match(/\/collections\/([^/?#]+)/i);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

function polygonRingCentroid(ring: unknown): [number, number] | null {
  if (!Array.isArray(ring)) return null;
  const validPoints = ring.filter(
    (p): p is [number, number] =>
      Array.isArray(p) && p.length >= 2 && Number.isFinite(p[0]) && Number.isFinite(p[1]),
  );
  if (!validPoints.length) return null;
  const sumLng = validPoints.reduce((s, p) => s + p[0], 0);
  const sumLat = validPoints.reduce((s, p) => s + p[1], 0);
  return [sumLng / validPoints.length, sumLat / validPoints.length];
}

function coordinateFromProperties(properties?: Record<string, unknown>): [number, number] | null {
  if (!properties) return null;
  const extent = properties['extent'] as
    | { spatial?: { bbox?: number[][] } }
    | undefined;
  const bbox = extent?.spatial?.bbox?.[0];
  if (Array.isArray(bbox) && bbox.length >= 4) {
    const [minLon, minLat, maxLon, maxLat] = bbox;
    if ([minLon, minLat, maxLon, maxLat].every((n) => Number.isFinite(n))) {
      return [(minLon + maxLon) / 2, (minLat + maxLat) / 2];
    }
  }
  const lng = Number(properties['lng'] ?? properties['lon'] ?? properties['longitude']);
  const lat = Number(properties['lat'] ?? properties['latitude']);
  if (Number.isFinite(lng) && Number.isFinite(lat)) {
    return [lng, lat];
  }
  return null;
}

/** Lon/lat coordinate for plotting a search-result marker (pin position). */
export function featureDisplayCoordinate(feature: CanvasOgcFeature): [number, number] | null {
  const geom = feature.geometry as { type?: string; coordinates?: unknown } | undefined;
  if (geom?.coordinates) {
    if (geom.type === 'Point' && Array.isArray(geom.coordinates) && geom.coordinates.length >= 2) {
      const [lng, lat] = geom.coordinates as number[];
      if (Number.isFinite(lng) && Number.isFinite(lat)) {
        return [lng, lat];
      }
    }

    if (geom.type === 'Polygon' && Array.isArray(geom.coordinates)) {
      const centroid = polygonRingCentroid((geom.coordinates as number[][][])[0]);
      if (centroid) return centroid;
    }

    if (geom.type === 'MultiPoint' && Array.isArray(geom.coordinates)) {
      const centroid = polygonRingCentroid(geom.coordinates);
      if (centroid) return centroid;
    }

    try {
      const format = new GeoJSON();
      const olGeom = format.readGeometry(geom, {
        dataProjection: 'EPSG:4326',
        featureProjection: 'EPSG:4326',
      });
      const extent = olGeom.getExtent();
      if (extent.every((n) => Number.isFinite(n))) {
        return [(extent[0] + extent[2]) / 2, (extent[1] + extent[3]) / 2];
      }
    } catch {
      /* fall through to property fallback */
    }
  }

  return coordinateFromProperties(feature.properties);
}

export interface BboxParsedSearchResource {
  resourceId: string;
  label: string;
  description: string;
  tags: string[];
  coordinate: [number, number];
  properties?: Record<string, unknown>;
}

/**
 * Parse OGC Records `FeatureCollection.features` into deduplicated search resources.
 * Marker id = dataset collection UUID from `links[rel=describes]`, not feature `id`.
 */
export function parseBboxItemsResponse(
  features: CanvasOgcFeature[],
  options?: { bbox?: OgcBbox; requireGeometryIntersect?: boolean },
): BboxParsedSearchResource[] {
  const filteredCollectionIds = new Set<string>();
  const filteredItems: CanvasOgcFeature[] = [];

  for (const item of features) {
    if (!item.geometry) continue;
    if (
      options?.bbox &&
      options.requireGeometryIntersect !== false &&
      !featureGeometryIntersectsBbox(item, options.bbox)
    ) {
      continue;
    }

    const describesLinks = (item.links ?? []).filter(
      (l) => String(l.rel ?? '').toLowerCase() === 'describes',
    );
    for (const link of describesLinks) {
      const id = extractCollectionIdFromHref(String(link.href ?? ''));
      if (id) filteredCollectionIds.add(id);
    }
    filteredItems.push(item);
  }

  const resources: BboxParsedSearchResource[] = [];
  for (const collectionId of filteredCollectionIds) {
    const sourceItem = filteredItems.find((item) =>
      (item.links ?? []).some((l) => {
        if (String(l.rel ?? '').toLowerCase() !== 'describes') return false;
        const id = extractCollectionIdFromHref(String(l.href ?? ''));
        return id === collectionId;
      }),
    );
    if (!sourceItem) continue;

    const coordinate = featureDisplayCoordinate(sourceItem);
    if (!coordinate) continue;

    const props = sourceItem.properties ?? {};
    resources.push({
      resourceId: collectionId,
      label: String(props['title'] ?? props['label'] ?? props['name'] ?? collectionId),
      description: String(props['description'] ?? ''),
      tags: Array.isArray(props['keywords'])
        ? (props['keywords'] as unknown[]).map(String)
        : [],
      coordinate,
      properties: props,
    });
  }

  return resources;
}

/** True when geometry is a simple axis-aligned box from `createBox()`. */
export function isAxisAlignedBox(geometry: Geometry | undefined): boolean {
  if (!(geometry instanceof Polygon)) return false;
  const ring = geometry.getLinearRing(0);
  if (!ring) return false;
  const coords = ring.getCoordinates();
  return coords.length === 5;
}
